import { Box, IconButton, Button } from "@mui/joy";
import { CloseIcon, WarningAmberRoundedIcon } from "../../styles/StyledMUI";
import type { AlertItem } from "../../api/zekay/alerts";
import {
  alertsDrawerCategoryChipSx,
  alertsDrawerCountSx,
  alertsDrawerItemGridSx,
  colorTokens,
  withAlpha,
} from "../../styles/globalStyles";
import DarkTypography from "./DarkTypography";

interface AlertsDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  alerts: AlertItem[];
  isError?: boolean;
  errorCount?: number;
  onResetError?: () => void;
  onAlertClick?: (alert: AlertItem) => void;
}

export default function AlertsDrawer({
  open,
  onClose,
  title = "Alertes",
  alerts,
  isError,
  errorCount,
  onResetError,
  onAlertClick,
}: AlertsDrawerProps) {
  const displayedAlerts = alerts || [];

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100dvh",
        width: { xs: "min(95vw, 520px)", md: 520 },
        backgroundColor: colorTokens.bg.app,
        borderLeft: `1px solid ${colorTokens.border.default}`,
        boxShadow: `-4px 0 16px ${withAlpha(colorTokens.bg.overlay, 0.45)}`,
        transform: `translateX(${open ? "0" : "100%"})`,
        transition: "transform 200ms ease",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
      }}
      aria-hidden={!open}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          bgcolor: colorTokens.bg.surface,
          borderBottom: `1px solid ${colorTokens.border.default}`,
        }}
      >
        <DarkTypography level="title-lg" colorVariant="title" sx={{ fontWeight: 700 }}>
          {title}
        </DarkTypography>
        <IconButton
          variant="plain"
          onClick={onClose}
          aria-label="Fermer"
          sx={{
            color: colorTokens.text.muted,
            "&:hover": { bgcolor: withAlpha(colorTokens.text.primary, 0.08) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {isError && (
        <Box
          sx={{
            p: 2,
            bgcolor: withAlpha(colorTokens.status.danger, 0.12),
            borderBottom: `1px solid ${colorTokens.border.default}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
            <WarningAmberRoundedIcon
              sx={{
                fontSize: "1.25rem",
                color: colorTokens.status.warning,
                flexShrink: 0,
                mt: 0.15,
              }}
              aria-hidden
            />
            <DarkTypography
              level="body-sm"
              sx={{ color: colorTokens.status.danger, fontWeight: 600 }}
            >
              Erreur de connexion API ({errorCount} tentatives)
            </DarkTypography>
          </Box>
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mb: 1.5, display: "block" }}>
            Les alertes sont temporairement désactivées.
          </DarkTypography>
          {onResetError && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                size="sm"
                variant="outlined"
                onClick={onResetError}
                sx={{
                  color: colorTokens.status.success,
                  borderColor: colorTokens.status.success,
                  "&:hover": {
                    bgcolor: withAlpha(colorTokens.status.success, 0.12),
                    borderColor: colorTokens.status.successHover,
                  },
                }}
              >
                Réactiver les alertes
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.2,
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {!isError && displayedAlerts.length === 0 && (
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ py: 2, textAlign: "center" }}>
            Aucune alerte active.
          </DarkTypography>
        )}
        {displayedAlerts.map((a) => {
          const tone =
            a.severity === "critical"
              ? colorTokens.status.danger
              : a.severity === "warning"
                ? colorTokens.status.warning
                : colorTokens.text.secondary;
          const category = a.category || "Équipement";

          return (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 1.2,
                borderRadius: "10px",
                border: `1px solid ${colorTokens.border.default}`,
                bgcolor: a.triggered
                  ? withAlpha(colorTokens.status.danger, 0.08)
                  : colorTokens.bg.surface,
                cursor: onAlertClick ? "pointer" : "default",
                "&:hover": onAlertClick
                  ? { bgcolor: withAlpha(colorTokens.status.danger, 0.14) }
                  : undefined,
              }}
              onClick={() => onAlertClick?.(a)}
            >
              <Box sx={alertsDrawerItemGridSx}>
                <DarkTypography
                  level="body-sm"
                  colorVariant="primary"
                  sx={{ fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" }}
                  component="span"
                >
                  {a.name}
                </DarkTypography>
                <DarkTypography
                  level="body-sm"
                  component="span"
                  sx={{ ...alertsDrawerCountSx, color: tone }}
                >
                  {a.count}
                </DarkTypography>
                <DarkTypography level="body-xs" component="span" sx={alertsDrawerCategoryChipSx}>
                  {category}
                </DarkTypography>
                {a.detail ? (
                  <DarkTypography
                    level="body-xs"
                    colorVariant="muted"
                    sx={{ minWidth: 0, gridColumn: 1 }}
                  >
                    {a.detail}
                  </DarkTypography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
